import { Sparkles } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { useAiCapabilities } from '../use-ai-capabilities';
import { AiKeyRequiredDialog } from './ai-key-required';

/**
 * Hộp "tính năng này làm gì — Thực thi / Huỷ" trước **mọi** lượt chạy AI.
 *
 * ## Vì sao không chạy ngay khi bấm
 *
 * Một lượt chạy tốn tiền thật, mất từ vài giây tới cả phút, và nhãn nút (vài
 * chữ trên thanh công cụ) không đủ để người dùng biết nó sẽ đọc gì và trả về
 * gì. Bấm nhầm thì không huỷ được tiền đã tiêu. Hộp này nói trước bằng lời,
 * rồi để người dùng quyết.
 *
 * ## Vì sao mô tả lấy từ `GET /ai/capabilities`
 *
 * Mỗi skill đã khai `title` + `description` tiếng Việt ở backend
 * (`AiSkill.description`), và capabilities trả sẵn chúng. Viết lại một bản ở
 * frontend là hai nguồn sự thật cho cùng một câu, và chúng sẽ trôi khỏi nhau
 * ngay lần đầu có người sửa skill. Không tìm thấy mô tả (AI chưa bật ở phạm vi
 * không có `projectId`) thì rơi về câu chung — hộp vẫn mở được.
 *
 * ## Vì sao portal
 *
 * Nút AI hay nằm **trong** một hộp thoại khác (form việc, sửa hàng loạt,
 * wizard kế hoạch). `Dialog` dùng `transform` để căn giữa, và một phần tử
 * `fixed` nằm dưới tổ tiên có `transform` thì định vị theo tổ tiên đó chứ
 * không theo màn hình — hộp xác nhận sẽ bị kẹt, cắt mép bên trong hộp cha.
 */
export function AiRunConfirmDialog({
  skill,
  projectId,
  fallbackTitle,
  onConfirm,
  onCancel,
}: {
  /** Tên skill, khớp `AiSkill.name` phía backend. */
  readonly skill: string;
  /** Có thì mô tả đọc theo key AI của dự án — xem `useAiCapabilities`. */
  readonly projectId?: string;
  /** Nhãn nút vừa bấm — dùng khi capabilities không có tiêu đề cho skill. */
  readonly fallbackTitle: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const { data } = useAiCapabilities(projectId);
  const descriptor = data?.skills.find((item) => item.name === skill);
  const title =
    descriptor?.title ??
    data?.manualFallbacks.find((item) => item.name === skill)?.title ??
    fallbackTitle;

  /*
    Esc = Huỷ. Nghe ở pha **capture** và chặn lan truyền: hộp cha (nếu có) cũng
    nghe Esc trên `window`, và một lần Esc không được đóng cả hai lớp — người
    dùng chỉ định thoát hộp xác nhận, không định vứt form đang gõ dở.
  */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onCancel();
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [onCancel]);

  return createPortal(
    // `z-[60]`: một ngữ cảnh xếp lớp riêng, cao hơn `z-50` của hộp cha.
    <div className="relative z-[60]">
      <div className="fixed inset-0 popup-scrim" onClick={onCancel} aria-hidden />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="ai-run-confirm-title"
        aria-describedby="ai-run-confirm-body"
        className="fixed left-1/2 top-1/2 flex max-h-[85vh] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col gap-3 overflow-y-auto rounded-DEFAULT border border-border bg-background p-4 shadow-xl glass-popup-md on-scrim"
      >
        <div className="flex items-start gap-2">
          <Sparkles aria-hidden className="mt-0.5 size-4 shrink-0 text-primary" />
          <h2 id="ai-run-confirm-title" className="text-base font-semibold">
            {title}
          </h2>
        </div>

        <div id="ai-run-confirm-body" className="flex flex-col gap-3 text-sm">
          <p>{descriptor?.description ?? t('ai.confirm.genericDescription')}</p>

          <div className="flex flex-col gap-1 rounded-DEFAULT bg-muted/50 p-3 text-muted-foreground">
            <span className="font-medium text-foreground">{t('ai.confirm.whatHappens')}</span>
            <ul className="flex list-disc flex-col gap-1 pl-4">
              <li>{t('ai.confirm.readsScope')}</li>
              <li>{t('ai.confirm.suggestOnly')}</li>
              <li>{t('ai.confirm.costAndTime')}</li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          {/* `autoFocus`: Enter là Thực thi, Esc là Huỷ — không cần chuột. */}
          <Button type="button" onClick={onConfirm} autoFocus>
            <Sparkles aria-hidden />
            {t('ai.confirm.run')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Cho những nút AI **không** dùng `AiAction` (nút tự dựng trong form, thanh
 * công cụ, ô chat): bọc hành động chạy trong `confirm(...)` rồi đặt `dialog`
 * vào cây. Cùng một hộp, cùng một câu chữ với `AiAction`.
 *
 * ```tsx
 * const aiConfirm = useAiRunConfirm(projectId);
 * <Button onClick={() => aiConfirm.confirm('task_breakdown', label, () => ask.mutate())} />
 * {aiConfirm.dialog}
 * ```
 */
export function useAiRunConfirm(projectId?: string): {
  readonly confirm: (skill: string, label: string, run: () => void) => void;
  readonly dialog: ReactNode;
} {
  const [pending, setPending] = useState<{
    skill: string;
    label: string;
    run: () => void;
  } | null>(null);

  /*
    Chưa có key thì hỏi key thay vì hỏi "Thực thi" — 2026-09-25. Nút gọi hook
    này nay luôn hiện, nên hộp xác nhận chạy AI không được mở ra cho một lượt
    chạy chắc chắn thất bại.
  */
  const { data: capabilities } = useAiCapabilities(projectId);
  const needsKey = capabilities !== undefined && !capabilities.enabled;
  const [askingKey, setAskingKey] = useState<{ skill: string; label: string } | null>(null);

  const confirm = useCallback(
    (skill: string, label: string, run: () => void) => {
      if (needsKey) setAskingKey({ skill, label });
      else setPending({ skill, label, run });
    },
    [needsKey],
  );
  const cancel = useCallback(() => setPending(null), []);

  if (askingKey) {
    return {
      confirm,
      dialog: (
        <AiKeyRequiredDialog
          skill={askingKey.skill}
          featureLabel={askingKey.label}
          onClose={() => setAskingKey(null)}
        />
      ),
    };
  }

  const dialog = pending ? (
    <AiRunConfirmDialog
      skill={pending.skill}
      fallbackTitle={pending.label}
      {...(projectId ? { projectId } : {})}
      onCancel={cancel}
      onConfirm={() => {
        setPending(null);
        pending.run();
      }}
    />
  ) : null;

  return { confirm, dialog };
}
